import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  BookOpen,
  Trophy,
  Award,
  Clock,
  TrendingUp,
  Play,
  ChevronRight,
  Star,
  Flame,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { enrollmentsAPI, coursesAPI, leaderboardAPI, recommendationsAPI, analyticsAPI } from '../lib/api';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [enrollRes, coursesRes, leaderboardRes] = await Promise.all([
        enrollmentsAPI.getMy(),
        coursesAPI.getAll({ published_only: true }),
        leaderboardAPI.get(null, 5),
      ]);

      setEnrollments(enrollRes.data);
      setLeaderboard(leaderboardRes.data);

      // Get recommended courses (not enrolled)
      const enrolledIds = enrollRes.data.map((e) => e.course_id);
      const notEnrolled = coursesRes.data.filter((c) => !enrolledIds.includes(c.id));
      setRecommendedCourses(notEnrolled.slice(0, 3));

      // Get stats for admin/manager
      if (isAdmin() || isManager()) {
        const statsRes = await analyticsAPI.getDashboard();
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const inProgressCourses = enrollments.filter((e) => !e.completed_at);
  const completedCourses = enrollments.filter((e) => e.completed_at);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-sketch text-4xl text-ink">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Continue your learning journey
            </p>
          </div>
          <Link to="/courses">
            <Button className="btn-sketchy" data-testid="browse-courses-btn">
              Browse Courses
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="stats-card" data-testid="stat-in-progress">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-muted-foreground font-medium">In Progress</span>
            </div>
            <p className="font-sketch text-4xl text-ink">{inProgressCourses.length}</p>
          </div>

          <div className="stats-card" data-testid="stat-completed">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-muted-foreground font-medium">Completed</span>
            </div>
            <p className="font-sketch text-4xl text-ink">{completedCourses.length}</p>
          </div>

          <div className="stats-card" data-testid="stat-points">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-muted-foreground font-medium">Points</span>
            </div>
            <p className="font-sketch text-4xl text-ink">{user?.points || 0}</p>
          </div>

          <div className="stats-card" data-testid="stat-badges">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-muted-foreground font-medium">Badges</span>
            </div>
            <p className="font-sketch text-4xl text-ink">{user?.badges?.length || 0}</p>
          </div>
        </div>

        {/* Admin Stats */}
        {stats && (isAdmin() || isManager()) && (
          <div className="card-sketchy bg-gradient-to-r from-blue-50 to-purple-50" data-testid="admin-stats">
            <h3 className="font-sketch text-2xl text-ink mb-4">Organization Overview</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg border border-black/10">
                <p className="font-sketch text-3xl text-primary">{stats.total_users}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-black/10">
                <p className="font-sketch text-3xl text-primary">{stats.total_courses}</p>
                <p className="text-sm text-muted-foreground">Active Courses</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-black/10">
                <p className="font-sketch text-3xl text-primary">{stats.total_enrollments}</p>
                <p className="text-sm text-muted-foreground">Enrollments</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-black/10">
                <p className="font-sketch text-3xl text-green-600">{stats.completion_rate}%</p>
                <p className="text-sm text-muted-foreground">Completion Rate</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Continue Learning */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-sketch text-2xl text-ink">Continue Learning</h2>
              <Link to="/my-learning" className="text-primary font-semibold hover:underline text-sm">
                View All
              </Link>
            </div>

            {inProgressCourses.length > 0 ? (
              <div className="space-y-4">
                {inProgressCourses.slice(0, 3).map((enrollment) => (
                  <CourseProgressCard key={enrollment.id} enrollment={enrollment} />
                ))}
              </div>
            ) : (
              <div className="card-sketchy text-center py-12">
                <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-sketch text-xl text-ink mb-2">No courses in progress</h3>
                <p className="text-muted-foreground mb-4">Start learning something new today!</p>
                <Link to="/courses">
                  <Button className="btn-sketchy">Browse Courses</Button>
                </Link>
              </div>
            )}

            {/* Recommended Courses */}
            {recommendedCourses.length > 0 && (
              <>
                <h2 className="font-sketch text-2xl text-ink mt-8">Recommended for You</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {recommendedCourses.map((course) => (
                    <RecommendedCourseCard key={course.id} course={course} />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Leaderboard */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-sketch text-2xl text-ink">Leaderboard</h2>
              <Link to="/leaderboard" className="text-primary font-semibold hover:underline text-sm">
                View All
              </Link>
            </div>
            <div className="card-sketchy space-y-3" data-testid="leaderboard-widget">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    entry.user_id === user?.id ? 'bg-primary/10 border border-primary' : 'bg-gray-50'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0
                        ? 'bg-yellow-400 text-black'
                        : index === 1
                        ? 'bg-gray-300 text-black'
                        : index === 2
                        ? 'bg-orange-400 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {entry.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{entry.name}</p>
                    <p className="text-xs text-muted-foreground">{entry.department || 'No dept'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{entry.points}</p>
                    <p className="text-xs text-muted-foreground">pts</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 sticky-note">
              <p className="font-sketch text-lg text-ink mb-3">Quick Tips</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Flame className="w-4 h-4 text-orange-500 mt-0.5" />
                  <span>Complete quizzes to earn more points</span>
                </li>
                <li className="flex items-start gap-2">
                  <Trophy className="w-4 h-4 text-yellow-500 mt-0.5" />
                  <span>Finish courses to unlock badges</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Climb the leaderboard!</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function CourseProgressCard({ enrollment }) {
  const [course, setCourse] = useState(null);

  useEffect(() => {
    coursesAPI.getById(enrollment.course_id).then((res) => setCourse(res.data));
  }, [enrollment.course_id]);

  if (!course) return null;

  return (
    <div className="card-sketchy flex flex-col sm:flex-row gap-4" data-testid={`course-progress-${enrollment.id}`}>
      <div className="sm:w-32 h-24 bg-gray-100 rounded-lg overflow-hidden border-2 border-black flex-shrink-0">
        {course.thumbnail_url ? (
          <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-ink mb-1">{course.title}</h3>
        <p className="text-sm text-muted-foreground mb-3">{course.category}</p>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Progress value={enrollment.progress} className="h-2" />
          </div>
          <span className="text-sm font-semibold text-primary">{Math.round(enrollment.progress)}%</span>
        </div>
      </div>
      <Link to={`/learn/${enrollment.id}`} className="sm:self-center">
        <Button className="btn-sketchy whitespace-nowrap" data-testid={`continue-btn-${enrollment.id}`}>
          <Play className="w-4 h-4 mr-1" />
          Continue
        </Button>
      </Link>
    </div>
  );
}

function RecommendedCourseCard({ course }) {
  return (
    <Link to={`/courses/${course.id}`}>
      <div className="card-sketchy course-card h-full" data-testid={`recommended-${course.id}`}>
        <div className="h-32 bg-gray-100 rounded-lg overflow-hidden border-2 border-black mb-4">
          {course.thumbnail_url ? (
            <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-muted-foreground" />
            </div>
          )}
        </div>
        <h3 className="font-bold text-ink mb-1 line-clamp-2">{course.title}</h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="bg-gray-100 px-2 py-1 rounded">{course.category}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {course.duration_hours}h
          </span>
        </div>
      </div>
    </Link>
  );
}
